import { useMemo, useState } from "react";

/**
 * Reusable table filter + sort hook.
 *
 * Usage:
 *   const t = useTableFilter(rows, { searchKeys: ["name","sku"], defaultSort: { key: "date", dir: "desc" } });
 *   <input value={t.search} onChange={(e)=>t.setSearch(e.target.value)} />
 *   <SortHeader tf={t} col="name">Nama</SortHeader>
 *   {t.rows.map(...)}
 */
export function useTableFilter(rows, opts = {}) {
  const searchKeys = opts.searchKeys || [];
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(opts.defaultSort || { key: null, dir: "asc" });

  const filtered = useMemo(() => {
    let out = Array.isArray(rows) ? [...rows] : [];
    const q = (search || "").trim().toLowerCase();
    if (q && searchKeys.length) {
      out = out.filter((r) =>
        searchKeys.some((k) => {
          const v = k.split(".").reduce((a, kk) => (a == null ? a : a[kk]), r);
          return v != null && String(v).toLowerCase().includes(q);
        })
      );
    }
    if (sort.key) {
      out.sort((a, b) => {
        const av = sort.key.split(".").reduce((x, k) => (x == null ? x : x[k]), a);
        const bv = sort.key.split(".").reduce((x, k) => (x == null ? x : x[k]), b);
        const na = Number(av), nb = Number(bv);
        let cmp;
        if (!isNaN(na) && !isNaN(nb) && av !== "" && bv !== "" && av != null && bv != null) {
          cmp = na - nb;
        } else {
          cmp = String(av ?? "").localeCompare(String(bv ?? ""), "id");
        }
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, search, sort, searchKeys.join(",")]);

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key !== key
        ? { key, dir: "asc" }
        : { key, dir: prev.dir === "asc" ? "desc" : "asc" }
    );
  };

  return { rows: filtered, search, setSearch, sort, setSort, toggleSort };
}
